import { withAuthenticationRequired } from "@auth0/auth0-react";
import {
  Divider,
  Grid,
  Box,
  IconButton,
  Stack,
  Tooltip,
  useTheme,
  Typography,
  Chip,
  alpha,
} from "@mui/material";
import { useSelector } from "react-redux";
import PageTitle from "../components/PageTitle";
import { useTranslation } from "react-i18next";
import { Edit, ArrowBack, Refresh } from "@mui/icons-material";
import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useBillingCheck } from "../hooks/useBillingCheck";
import DocumentTitle from "../components/DocumentTitle";
import LabelEditDialog from "../components/Dialogs/labelsEdit";
import useLabels, { useLabelDevices } from "../hooks/useLabels";
import DeviceCard from "../components/Cards/Device";
import { useDevices } from "../hooks/queries";
import DevicesMap from "../components/Maps/Devices";
import { isTracker } from "../utils/deviceUtils";

const hasValidCoordinates = (device) => {
  const lng = Number(device?.lng);
  const lat = Number(device?.lat);

  if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
    return false;
  }

  if (lng === 0 || lat === 0) {
    return false;
  }

  if (lng < -180 || lng > 180) {
    return false;
  }

  if (lat < -90 || lat > 90) {
    return false;
  }

  return true;
};

function Label() {
  useBillingCheck();

  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation("common");

  const selectedOrganisation = useSelector(
    (state) => state.selectedOrganisation
  );

  const { data: allLabels = [] } = useLabels(selectedOrganisation?.id);
  const label = allLabels.find((l) => String(l.id) === String(id));

  const { data: labelDevices = [], refetch: refetchLabelDevices } =
    useLabelDevices(selectedOrganisation ? id : null);

  const { data: devices = [] } = useDevices(selectedOrganisation?.id);

  // Map label device references to full device objects, falling back to the label device data itself
  const fullDevices = useMemo(() => {
    if (!labelDevices.length) return [];
    if (!devices.length) return labelDevices;
    return labelDevices
      .map(
        (ld) =>
          devices.find((d) => d.id === ld.id || d.id === ld.device_id) || ld
      )
      .filter(Boolean);
  }, [labelDevices, devices]);

  const trackerDevices = useMemo(
    () => fullDevices.filter((device) => isTracker(device?.device_type)),
    [fullDevices]
  );

  const mappableTrackerDevices = useMemo(
    () => trackerDevices.filter(hasValidCoordinates),
    [trackerDevices]
  );

  const [editOpen, setEditOpen] = useState(false);

  const handleRefresh = () => {
    refetchLabelDevices();
  };

  return (
    <>
      <DocumentTitle
        title={label?.name || t("labels.index")}
        selectedOrganisation={selectedOrganisation}
      />

      {mappableTrackerDevices.length > 0 && (
        <Box
          sx={{
            mt: "-16px",
            mx: "-16px",
            width: "calc(100% + 32px)",
            height: "65vh",
            overflow: "hidden",
            mb: 2,
          }}
        >
          <DevicesMap devices={mappableTrackerDevices} />
        </Box>
      )}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <PageTitle
              theme={theme}
              title={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  {label?.name || t("labels.index")}
                  {label?.color && (
                    <Chip
                      size="small"
                      label={label.name}
                      sx={{
                        backgroundColor: alpha(label.color, 0.15),
                        color: label.color,
                        fontWeight: 600,
                        borderRadius: "16px",
                        fontSize: "0.75rem",
                      }}
                    />
                  )}
                </Box>
              }
              subTitle={t("labels.devices_assigned_count", {
                count: fullDevices.length,
              })}
            />

            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title={t("common.back")} placement="top" arrow>
                <IconButton size="small" onClick={() => navigate("/label")}>
                  <ArrowBack />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("labels.edit")} placement="top" arrow>
                <IconButton size="small" onClick={() => setEditOpen(true)}>
                  <Edit />
                </IconButton>
              </Tooltip>
              <Divider orientation="vertical" flexItem />
              <Tooltip title={t("common.refresh")} placement="top" arrow>
                <IconButton size="small" onClick={handleRefresh}>
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        </Grid>

        {fullDevices.map((device, index) => (
          <DeviceCard
            key={`${device.id || device.dev_eui || "device"}-${index}`}
            device={device}
          />
        ))}
        {fullDevices.length === 0 && (
          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                textAlign: "center",
                py: 6,
              }}
            >
              <Typography variant="body1" color="text.secondary">
                {t("labels.no_devices")}
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>

      {label && (
        <LabelEditDialog
          open={editOpen}
          handleClose={() => {
            setEditOpen(false);
            handleRefresh();
          }}
          label={label}
        />
      )}
    </>
  );
}

export default withAuthenticationRequired(Label);
